import { create } from 'zustand';

import { db } from '@/services/storage/db';
import { duplicateTemplate as duplicateTemplateService } from '@/services/template-service';
import type { ChecklistTemplate, CreateTemplateInput, UpdateTemplateInput } from '@/types/checklist-template';
import type { WeaponType } from '@/types/season';

interface TemplateStore {
  templates: ChecklistTemplate[];
  selectedTemplate: ChecklistTemplate | null;
  isLoading: boolean;
  error: string | null;
  loadTemplates: () => Promise<void>;
  loadTemplatesByWeaponType: (weaponType: WeaponType) => Promise<void>;
  getTemplatesByWeapon: (weaponType: WeaponType) => ChecklistTemplate[];
  getTemplate: (id: string) => ChecklistTemplate | undefined;
  selectTemplate: (id: string) => Promise<void>;
  addTemplate: (data: CreateTemplateInput) => Promise<ChecklistTemplate>;
  updateTemplate: (id: string, data: UpdateTemplateInput) => Promise<ChecklistTemplate>;
  duplicateTemplate: (id: string) => Promise<ChecklistTemplate>;
  removeTemplate: (id: string) => Promise<void>;
}

export const useTemplateStore = create<TemplateStore>((set, get) => ({
  templates: [],
  selectedTemplate: null,
  isLoading: false,
  error: null,

  loadTemplates: async () => {
    set({ isLoading: true, error: null });
    try {
      const templates = await db.getTemplates();
      set({ templates, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load templates';
      console.error('TemplateStore.loadTemplates:', err);
      set({ error: message, isLoading: false });
    }
  },

  getTemplatesByWeapon: (weaponType) => get().templates.filter((t) => t.weaponType === weaponType),

  getTemplate: (id) => get().templates.find((t) => t.id === id),

  loadTemplatesByWeaponType: async (weaponType) => {
    set({ isLoading: true, error: null });
    try {
      const templates = await db.getTemplatesByWeaponType(weaponType);
      set({ templates, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load templates';
      console.error('TemplateStore.loadTemplatesByWeaponType:', err);
      set({ error: message, isLoading: false });
    }
  },

  selectTemplate: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const selectedTemplate = await db.getTemplate(id);
      set({ selectedTemplate, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load template';
      console.error('TemplateStore.selectTemplate:', err);
      set({ error: message, isLoading: false });
    }
  },

  addTemplate: async (data) => {
    try {
      const template = await db.createTemplate(data);
      set((state) => ({ templates: [...state.templates, template] }));
      return template;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create template';
      console.error('TemplateStore.addTemplate:', err);
      set({ error: message });
      throw err;
    }
  },

  updateTemplate: async (id, data) => {
    try {
      const updated = await db.updateTemplate(id, data);
      set((state) => ({
        templates: state.templates.map((t) => (t.id === id ? updated : t)),
      }));
      return updated;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update template';
      console.error('TemplateStore.updateTemplate:', err);
      set({ error: message });
      throw err;
    }
  },

  duplicateTemplate: async (id) => {
    try {
      const copy = await duplicateTemplateService(id);
      set((state) => ({ templates: [...state.templates, copy] }));
      return copy;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to duplicate template';
      console.error('TemplateStore.duplicateTemplate:', err);
      set({ error: message });
      throw err;
    }
  },

  removeTemplate: async (id) => {
    try {
      await db.deleteTemplate(id);
      set((state) => ({
        templates: state.templates.filter((t) => t.id !== id),
        selectedTemplate: null,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete template';
      console.error('TemplateStore.removeTemplate:', err);
      set({ error: message });
      throw err;
    }
  },
}));
